import { html, nothing } from 'lit/html.js';
import { classMap } from 'lit/directives/class-map.js';
import { selectMask } from '@fil-global/shared/src/select-dropdown';
import { dispatchEvent } from '@fil-global/shared/src/util';

// TODO remove fil-icon
import { TextField } from '@fil-global/text-field';
import '@fil-global/fil-button';
import '@fil-global/fil-icon';
import '@fil-global/fil-link';

import componentStyles from './fil-select.styles.js';

/**
 * @tag fil-select
 * @property {"primary"|"secondary"} [appearance='primary']
 * @property {object} [allOption] Data for option items {value, label, selected, subtext}
 * @property {boolean} [checkboxAlignment=false] Alignment of the checkbox in option.
 * @property {boolean} [dropRight] When set, positions the dropdown menu to the right of the select and dropdown menu minimal width is select width.
 * @property {boolean} [dropLeft] When set, positions the dropdown menu to the left of the select and dropdown menu minimal width is select width.
 * @property {object} [extraProps] Extra props to be used in the component
 * @property {array} [footer] Footer options in the component
 * @property {boolean} [icon=false] Icon in the component
 * @property {string} [label] Label of the component
 * @property {"1-col"|"2-cols"} [layout="1-col"]
 * @property {"currency"|"dob"|"nationalInsuranceNumber"} [mask]
 * @property {array} [options] List of option to be passed in the component
 * @property {string} [placeholder] Placeholder of the component
 * @property {boolean} [searchable] Whether to show the search bar
 * @property {string} [selectedText="Options selected"] Multiple selected text
 * @property {'standard'|'small'} [size="standard"] Size of the component
 * @property {"disabled"|"invalid"|"valid"} [status]
 * @property {'single'|'multiple'} [type="single"] Type of the component
 * @property {array} [value] Value of the component
 * @fires change.gds.select (event: Event) => void
 *
 */
export class FilSelect extends TextField {
  static properties = {
    allOption: { type: Object }, // all option for multiple select
    checkboxAlignment: { type: Boolean },
    dropRight: { type: Boolean },
    dropLeft: { type: Boolean },
    extraProps: { type: Object }, // for properties that initialize select
    footer: { type: Array }, // for showing apply and cancel buttons.
    icon: { type: Boolean },
    options: { type: Array },
    placeholder: { type: String },
    searchable: { type: Boolean },
    selectedText: { type: String }, // multiple selected text
    size: { type: String }, // 'small' | 'standard'
    type: { type: String },
    value: { type: Array },
    required: { type: Boolean },
  };

  static styles = [super.styles, componentStyles];

  constructor() {
    super();
    this.footer = [];
    this.selectedText = 'Options selected';
    this.size = 'standard';
    this.type = 'single';
    this.value = [];

    // the following properties are not reactive properties, their changes do not trigger update cycle.
    this.currentValue = this.value;
    this.selectDOM = null;
    this.placeholderValue = '';
  }

  getFooter() {
    if (this.footer.length === 0 || this.type !== 'multiple') {
      return;
    }

    if (this.selectDOM.querySelector('.fil-choices--footer')) {
      this.selectDOM.querySelector('.fil-choices--footer').remove();
    }

    this.selectDOM.querySelector('.fil-choices__list--dropdown').insertAdjacentHTML(
      'beforeend',
      `<div class="fil-choices--footer">
            <fil-link size="${this.size}" ${
              this.value != null && this.value.length ? '' : 'disabled'
            }>
            ${this.footer[0]?.label || 'Clear'}
            </fil-link>
            <fil-button kind="secondary" label="${
              this.footer[1]?.label || 'Apply'
            }" size="${this.size}" ${
              this.value !== null && !this.areArraysSame(this.currentValue, this.value)
                ? ''
                : 'disabled'
            }></fil-button>
        </div>`,
    );

    // bind events to footer items
    this.selectDOM.querySelector('.fil-choices--footer fil-link').addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      this.selectDOM.querySelector('#removedItem button')?.click();
      this.choice.unhighlightAll();
    });

    this.selectDOM.querySelector('.fil-choices--footer fil-button').addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.target.hasAttribute('disabled')) {
        this.currentValue = this.value;
        dispatchEvent(this, 'change.gds.select', { value: this.value });
      }
      this.choice.hideDropdown();
    });
  }

  areArraysSame(array1, array2) {
    return (
      array1 &&
      array2 &&
      array1.length === array2.length &&
      array1.every(element => array2.includes(element))
    );
  }

  _createChoiceInstance() {
    this.choice = null;
    this.selectDOM = null;

    const option = {
      allowHTML: true,
      placeholder: !!this.placeholder,
      ...(this.placeholder && { placeholderValue: this.placeholderValue }),
      ...(this.type === 'single' &&
        this.searchable && { searchEnabled: true, searchFields: ['customProperties', 'value'] }),
      ...this.extraProps,
      shadowRoot: this.shadowRoot,
    };

    this.choice = selectMask('select', option, this.type === 'multiple');
    this.selectDOM = this.shadowRoot.querySelector('.fil-choices');
    // Since we customized the choices.js, we need to remove the role and aria-expanded attributes for fixing a11y issues
    this.selectDOM.setAttribute('aria-label', 'select option: ' + this.value);

    if (this.searchable && this.type === 'single') {
      const { containerInner, input, containerOuter } = this.choice;
      if (containerInner && input) {
        const innerElement = containerInner.element;
        const searchInputElement = input.element;
        searchInputElement.removeAttribute('role');
        innerElement.appendChild(searchInputElement);
        containerOuter?.element.addEventListener('click', () => {
          searchInputElement.focus();
        });
      }
    }

    if (this.type === 'multiple') {
      this.getFooter();
      this.handlePlaceholder();

      if (this.allOption) {
        this._setAllOption();
      }

      if (this.footer.length) {
        this.selectDOM.addEventListener('showDropdown', () => {
          this.currentValue = this.choice.getValue(true);
        });
      }

      // fix a11y issue
      const hideSearchInput = this.selectDOM.querySelector('.fil-choices__inner input');
      hideSearchInput?.removeAttribute('role');
      hideSearchInput?.setAttribute('aria-label', 'search dropdown list');
      const hideSelect = this.selectDOM.querySelector('.fil-choices__inner select');
      hideSelect?.nextElementSibling?.removeAttribute('role');
    }

    // if single select has no placeholder, no default value, the first item should be selected, so the value need to be reset to the first item value
    if (this.type === 'single' && !this.placeholder && this.value?.length === 0) {
      const firstItemValue = this.choice.getValue(true);
      this.value = [firstItemValue];
    }

    // set preselect items
    this.setChoiceValue();
  }

  firstUpdated() {
    this._createChoiceInstance();
  }

  onChange() {
    this.value = this.choice.getValue(true);

    if (this.allOption && this.type === 'multiple') {
      const allInput = this.shadowRoot.querySelector('#allOption');
      let allItemLength = 0;
      this.options.forEach(item => {
        if (item.choices) {
          allItemLength += item.choices.length;
        } else {
          allItemLength += 1;
        }
      });

      if (this.value.length === allItemLength) {
        allInput.indeterminate = false;
        allInput.checked = true;
      } else if (this.value.length > 0 && this.value.length < allItemLength) {
        allInput.indeterminate = true;
      } else if (allInput.checked) {
        allInput.indeterminate = false;
        allInput.checked = false;
      }
    }
  }

  _resetChoices() {
    const resizeChoices = this.options.map(item => {
      const { choices, ...remain } = item;
      if (choices && choices.length > 0) {
        const newItem = { ...remain };
        const newChoices = choices.map(option => {
          const { label, ...others } = option;
          const newOption = { ...others, customProperties: label };
          newOption.label = this._renderOptionContent(option, 'rebuild');
          return newOption;
        });
        newItem.choices = newChoices;
        return newItem;
      }
      const { label, ...others } = item;
      const newOption = { ...others, customProperties: label };
      newOption.label = this._renderOptionContent(item, 'rebuild');

      return newOption;
    });

    if (this.type === 'single' && this.placeholder) {
      const placeHolderOption = {
        label: this.placeholder,
        value: '',
        customProperties: this.placeholder,
      };
      const newArray = [placeHolderOption].concat(resizeChoices);
      this.choice.setChoices(newArray, 'value', 'label', true);
    } else {
      this.choice.setChoices(resizeChoices, 'value', 'label', true);
    }

    this.setChoiceValue();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('type') && changedProperties.get('type') !== undefined) {
      this.value = [];
      this.choice.destroy();
      this._createChoiceInstance();
    }

    // options changed but not the first time. the first time is handled in firstUpdated();
    if (changedProperties.has('options') && changedProperties.get('options') !== undefined) {
      const { options, type, allOption } = this;
      if (options) {
        // for multiple, reset the selected value if options list is changed
        if (type === 'multiple') {
          const { value } = this;
          if (value && Array.isArray(value) && value.length > 0) {
            this.value = [];
            this.handlePlaceholder();
            if (allOption) {
              const allOptionInput = this.shadowRoot.querySelector('#allOption');
              allOptionInput.checked = false;
            }
          }
        }
        this._resetChoices();
      }
    }

    if (changedProperties.has('disabled') || changedProperties.has('status')) {
      if (this.disabled || this.status === 'disabled') {
        this.choice.disable();
      } else {
        this.choice.enable();
      }
    }

    if (changedProperties.has('size') && changedProperties.get('size') !== undefined && this.icon) {
      if (this.size !== undefined && this.options) {
        this._resetChoices();
      }
    }

    if (changedProperties.has('checkboxAlignment') && this.checkboxAlignment !== undefined) {
      this.choice.destroy();
      this._createChoiceInstance();
    }

    if (
      changedProperties.has('searchable') &&
      this.type === 'single' &&
      changedProperties.get('searchable') !== undefined
    ) {
      this.choice.destroy();
      this._createChoiceInstance();
    }

    if (
      changedProperties.has('value') &&
      changedProperties.get('value') !== undefined &&
      ((this.type === 'single' && changedProperties.get('value') !== this.value[0]) ||
        (this.type === 'multiple' &&
          !this.areArraysSame(changedProperties.get('value'), this.value)))
    ) {
      const selectedValue = this.setChoiceValue();

      if (!this.footer.length) {
        dispatchEvent(this, 'change.gds.select', { value: selectedValue });
      }
    }

    this.getFooter();
  }

  // set choice value
  setChoiceValue() {
    let selectedValue = this.value !== null && this.value !== undefined ? this.value : [];
    const isPlaceholderSelected = this.type === 'single' && selectedValue === this.placeholderValue;

    if (typeof selectedValue === 'string') {
      selectedValue = [this.value];
    }

    if (this.type === 'multiple') {
      this.handleRemovableEle(selectedValue.length);
      if (selectedValue.length > 0) {
        this.handlePlaceholder('remove');
      } else {
        this.handlePlaceholder();
      }
      this.choice.removeActiveItems();
    }

    this.choice.setChoiceByValue(selectedValue);

    return isPlaceholderSelected ? [] : selectedValue;
  }

  // add or remove placeholder node
  handlePlaceholder(operation) {
    const inner = this.shadowRoot.querySelector('.fil-choices__inner');

    if (!operation) {
      const placeholderNode = inner.querySelector('#placeholder');
      if (!placeholderNode) {
        const placeholderSpan = document.createElement('span');
        placeholderSpan.setAttribute('id', 'placeholder');
        placeholderSpan.textContent = this.placeholder ? this.placeholder : 'Select options';
        inner.appendChild(placeholderSpan);
      }
    } else {
      const placeholderNode = inner.querySelector('#placeholder');
      if (placeholderNode) {
        placeholderNode.remove();
      }
    }
  }

  handleRemovableEle(counts) {
    const multipleDemo = this.shadowRoot.querySelector('.fil-choices__inner');
    const input = multipleDemo.querySelector('input');
    // check whether we have removable nodes
    const removableNode = multipleDemo.querySelector('#removedItem');

    if (counts === 0) {
      if (removableNode) {
        removableNode.remove();
      }
    } else if (!removableNode) {
      const actionWrapper = document.createElement('div');
      actionWrapper.setAttribute('id', 'removedItem');
      actionWrapper.setAttribute('class', 'fil-choices__list fil-choices__list--multiple');
      actionWrapper.innerHTML = `<div class="fil-choices__item fil-choices__item--removable"><span>${counts}</span><button aria-label="button for deleted selected options"><fil-icon icon="close" size="12"></fil-icon></button></div><span>${this.selectedText}</span>`;
      const btn = actionWrapper.querySelector('button');

      // clear all selected items
      const clearAll = e => {
        this.choice.removeActiveItems();
        e.stopPropagation();
        actionWrapper.remove();
        this.handlePlaceholder();
        this.value = [];
        if (this.allOption) {
          const allInput = this.shadowRoot.querySelector('#allOption');
          allInput.checked = false;
          allInput.indeterminate = false;
        }
        if (this.footer.length && !this.areArraysSame(this.currentValue, this.value)) {
          this.currentValue = this.value;
          dispatchEvent(this, 'change.gds.select', { value: this.value });
        }
      };

      if (!this.disabled) {
        btn.addEventListener('click', e => {
          clearAll(e);
        });
        btn.addEventListener('keydown', e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            clearAll(e);
          }
        });
      }
      multipleDemo.insertBefore(actionWrapper, input);
    } else {
      const selectedCountNode = removableNode.querySelector('span');
      selectedCountNode.textContent = counts;
    }
  }

  _setAllOption() {
    const dropdown = this.shadowRoot.querySelector('.fil-choices__list--dropdown');
    if (dropdown) {
      // set default values
      const { value = 'all', label = 'All', selected, subtext } = this.allOption;
      const allOption = document.createElement('div');
      allOption.className = `fil-choices__item fil-choices__item--choice fil-choices__item--selectable`;
      allOption.innerHTML = `<div class="fil-checkbox">
        <input
          type="checkbox"
          class="fil-checkbox__field"
          value="${value}"
          ${selected && 'checked'}
          id="allOption"
        />
        <span class="fil-checkbox__label ${
          this.checkboxAlignment ? 'fil-checkbox__label--right' : ''
        }">
          <strong>${label}</strong>${subtext ? `<span>${subtext}</span>` : ''}
        </span>
      </div>`;
      allOption.setAttribute('data-select-text', true);
      dropdown.insertBefore(allOption, dropdown.firstChild);

      allOption.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        const input = allOption.querySelector('input');
        input.indeterminate = false;
        const isChecked = input.checked;
        input.checked = !isChecked;
        if (!isChecked) {
          const values = this.options.map(item => {
            if (item.choices) {
              return item.choices.map(option => option.value);
            }
            return item.value;
          });

          this.value = [].concat(...values);
        } else {
          this.value = [];
        }
      });
    }
  }

  _resetCheckboxPosition() {
    // reset all option checkbox position
    const allOptionInput = this.shadowRoot.querySelector('#allOption');
    const sibling = allOptionInput.nextElementSibling;
    sibling.classList.remove('fil-checkbox__label--right');
    if (this.checkboxAlignment) {
      sibling.classList.add(`fil-checkbox__label--right`);
    }
  }

  _renderOptionContent(option, rebuild) {
    if (this.type === 'single') {
      if (option.icon && this.icon) {
        if (rebuild) {
          return `<fil-icon
            icon=${option.icon}
            size="${this.size === 'small' ? 16 : 20}"
          ></fil-icon
          >${option.label}`;
        }
        return html`<fil-icon
            icon=${option.icon}
            size="${this.size === 'small' ? 16 : 20}"
          ></fil-icon
          >${option.label}`;
      }
    }

    if (this.type === 'multiple') {
      if (rebuild) {
        return `<span class="fil-checkbox__label ${
          this.checkboxAlignment ? 'fil-checkbox__label--right' : ''
        }">${
          option.subtext
            ? `<strong>${option.label}</strong><span class="subtext">${option.subtext}</span>`
            : option.label
        }</span>`;
      }

      return html`<span
        class="fil-checkbox__label ${this.checkboxAlignment ? 'fil-checkbox__label--right' : ''}"
        >${option.subtext
          ? html`<strong>${option.label}</strong><span class="subtext">${option.subtext}</span>`
          : option.label}</span
      >`;
    }

    if (rebuild) {
      return `${
        option.subtext
          ? `<strong>${option.label}</strong><span class="subtext">${option.subtext}</span>`
          : option.label
      }`;
    }

    return html`${option.subtext
      ? html`<strong>${option.label}</strong><span class="subtext">${option.subtext}</span>`
      : option.label}`;
  }

  renderIcon() {
    if (this.status === 'valid' || this.status === 'invalid') {
      return html`<fil-icon
        icon=${this.status === 'valid' ? 'custom-check-circle-1' : 'custom-alert-circle'}
        size="${this.size === 'small' ? 16 : 20}"
        filled
        class="fil-text-field__icon"
      ></fil-icon>`;
    }

    return null;
  }

  _renderOption(option) {
    return html`<option
      value=${option.value}
      ?disabled=${option.disabled}
      ?selected=${option.selected}
      id=${`fil-option-${option.value}`}
      data-custom-properties="${option.label}"
      role="option"
    >
      ${this._renderOptionContent(option)}
    </option>`;
  }

  _renderOptions() {
    const { options } = this;

    return html`${options.map(item => {
      if (item.choices) {
        return html`<optgroup label=${item.label}>
          ${item.choices.map(option => html`${this._renderOption(option)}`)}
        </optgroup>`;
      }

      return html`${this._renderOption(item)}`;
    })}`;
  }

  _renderPlaceholder() {
    if (this.type === 'single' && this.placeholder) {
      return html`<option
        value=${this.placeholderValue}
        data-custom-properties="${this.placeholder}"
        role="option"
      >
        ${this.placeholder}
      </option>`;
    }

    return nothing;
  }

  renderElement() {
    const classes = {
      [`fil-choice-wrapper--small`]: this.size === 'small',
      'fil-choice-searchable': !!this.searchable,
      'is-expanded--right': this.dropRight,
      'is-expanded--left': this.dropLeft,
    };
    return html`
      <div class="fil-choice-wrapper${classMap(classes)}">
        <select
          ?multiple=${this.type === 'multiple'}
          @change=${this.onChange}
          ?required=${this.required}
          aria-required=${!!this.required}
        >
          ${this._renderPlaceholder()}${this._renderOptions()}
        </select>
        ${this.renderIcon()}
      </div>
    `;
  }
}

export default FilSelect;
